import React, {useMemo} from 'react';
import {classes, style} from './doppe-viewer-action-modal.st.css';
import {DoppeActionView} from '../../doppe-sdk';
import {FlexLayout} from '@wix/devzai-common-client';
import {useObservableValue} from '@wix/devzai-utils-react';
import {DoppeViewerModalPanel, DoppeViewerModalPanelLayout} from '../doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {DoppeViewerActionPopup} from '../doppe-viewer-action-popup/doppe-viewer-action-popup';
import {
    BymoPageLayout,
    BymoPageStyleScope,
    BymoPageStyleScopeContext,
    useBymoPageLayout
} from '../bymo-page-style/bymo-page-style';
import {
    WidgetRenderingContext,
    WidgetRenderingContextType
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {assertDefined, evaluateFunction, ObservableValue, OmitStrict} from '@wix/devzai-utils-common';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {DoppeDtoAction} from '../../client-server-common/types/doppe-dto-action';
import {doppeHideableValueIsVisible, useBymoPageProps} from '../../client-server-common';

export const DoppeViewerActionModal = React.memo(function DoppeViewerActionModal(props: DoppeViewerActionModal.Props) {
    const {
        showRootBackButton,
        showRootCloseButton,

        closeCallback,
        openedFromSearch = false,
        actionId,
        defaultListItemIdToRender,
        mobileLayout = DoppeViewerModalPanelLayout.FullScreen,
        desktopLayout = DoppeViewerModalPanelLayout.Inner,
        ...actionViewerProps
    } = props;

    const clientServices = useDoppeViewerClientServices();

    const bymoPageProps = useBymoPageProps();

    const action = useMemo(() => {
        return assertDefined(bymoPageProps.actions.find(action => action.id === actionId));
    }, [bymoPageProps.actions]);

    const actionType = assertDefined(
        clientServices.actionTypesStore.getRegisteredActionType(action.actionTypeId),
        `Action type with id '${action.actionTypeId}' isn't registered`);

    const observableCurrentItemIdToRender = useMemo(() => {
        return actionType.resolveListItems ? new ObservableValue(defaultListItemIdToRender ?? null) : undefined;
    }, [defaultListItemIdToRender, actionType]);


    const renderingInnerStateController = useMemo(() => {
        return new ObservableValue(false);
    }, []);

    const canGoBack = () => {
        return renderingInnerStateController.getValue() && !defaultListItemIdToRender;
    };

    const goBack = () => {
        renderingInnerStateController.setValue(false);
        observableCurrentItemIdToRender?.setValue(null);
    };

    const isInnerWidgetRendered = useObservableValue(renderingInnerStateController);

    const layout = useBymoPageLayout();
    const isMobile = layout === BymoPageLayout.Narrow;
    const isFullScreen = (DoppeViewerModalPanelLayout.FullScreen === mobileLayout && isMobile) || (DoppeViewerModalPanelLayout.FullScreen === desktopLayout && !isMobile)

    const searchTitle = doppeHideableValueIsVisible(action.searchSettings?.title) ? action.searchSettings?.title : undefined;

    const {
        title,
        description,
    } = evaluateFunction(() => {

        if (defaultListItemIdToRender) {
            return {
                title: (actionType.resolveListItems ?
                    actionType.resolveListItems(action).find(item => item.itemId === defaultListItemIdToRender)?.title :
                    (openedFromSearch ? searchTitle : undefined)) ?? action.name,
                description: undefined,
            }
        }
        else if (openedFromSearch) {
            return {
                title: searchTitle ?? action.name,
                description: undefined,
            }
        }
        else {
            return {
                title: action.name,
                description: undefined,
            }
        }
    })

    return (
        <BymoPageStyleScopeContext.Provider
            value={BymoPageStyleScope.Popup}
        >
            <DoppeViewerModalPanel
                className={FlexLayout.column.default}
                mobileLayout={mobileLayout}
                desktopLayout={desktopLayout}
            >
                <WidgetRenderingContext.Provider
                    value={{
                        widgetRenderingContextType: WidgetRenderingContextType.FullScreenModal,
                        renderingInnerStateController: renderingInnerStateController,
                        observableCurrentItemIdToRender: observableCurrentItemIdToRender,
                        isFullScreen: isFullScreen,
                        headerCustomSettings: {
                            backButtonCallback: isInnerWidgetRendered || (showRootBackButton && isFullScreen) ? () => {
                                canGoBack() ? goBack() : closeCallback?.()
                            } : undefined,
                            dismissButtonCallback: (!isFullScreen || showRootCloseButton) ? closeCallback : undefined,
                            title: title,
                            subtitle: description,
                        }
                    }}
                >
                    <DoppeViewerActionPopup
                        className={style(classes.actionPopup, {}, FlexLayout.fillRemainingVerticalSpace)}
                        {...actionViewerProps}
                        action={action}
                    />
                </WidgetRenderingContext.Provider>
            </DoppeViewerModalPanel>
        </BymoPageStyleScopeContext.Provider>
    )
});

export namespace DoppeViewerActionModal {

    export interface ActionViewRenderProps {
        actionViewContextProps: DoppeActionView.ActionContextProps;
    }

    export interface Props extends OmitStrict<DoppeViewerActionPopup.Props, 'action'> {
        openedFromSearch: boolean;
        actionId: DoppeDtoAction.Id;
        showRootBackButton?: boolean;
        showRootCloseButton?: boolean;
        closeCallback?: () => void;
        defaultListItemIdToRender?: string;
        mobileLayout?: DoppeViewerModalPanelLayout;
        desktopLayout?: DoppeViewerModalPanelLayout;
    }

}